import { useEffect, useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { Plus, Trash2, Car } from 'lucide-react';

/**
 * Editable list of compatibility entries.
 * value = [{ make, model, years: number[], trims: string[], engines: string[] }, ...]
 * Calls onChange(newValue) on any change.
 */
export function FitmentEditor({ value, onChange }) {
    const list = Array.isArray(value) ? value : [];
    const [vehicles, setVehicles] = useState({});

    useEffect(() => {
        api.get('/vehicles').then(({ data }) => setVehicles(data || {})).catch(() => {});
    }, []);

    const addRow = () => {
        const makes = Object.keys(vehicles);
        const make = makes[0] || '';
        const model = make ? Object.keys(vehicles[make] || {})[0] || '' : '';
        const _key = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        onChange([...list, { _key, make, model, years: [], trims: [], engines: [] }]);
    };

    const removeRow = (idx) => onChange(list.filter((_, i) => i !== idx));

    const update = (idx, patch) => onChange(list.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-white/50 inline-flex items-center gap-1.5">
                    <Car className="w-3.5 h-3.5" /> Fitment ({list.length} {list.length === 1 ? 'platform' : 'platforms'})
                </div>
                <button
                    type="button"
                    onClick={addRow}
                    data-testid="fitment-add-row"
                    className="text-xs px-2.5 py-1.5 rounded-md bg-amber-500/20 border border-amber-500/40 text-amber-200 inline-flex items-center gap-1.5"
                >
                    <Plus className="w-3.5 h-3.5" /> Add platform
                </button>
            </div>
            {list.length === 0 && (
                <div className="text-xs text-white/40 italic">
                    No fitment listed. This part will not show up under any vehicle filter until a platform is added.
                </div>
            )}
            <div className="space-y-3">
                {list.map((entry, idx) => (
                    <FitmentRow
                        key={entry._key || `${entry.make || 'm'}-${entry.model || 'mo'}-${idx}`}
                        entry={entry}
                        vehicles={vehicles}
                        onChange={(patch) => update(idx, patch)}
                        onRemove={() => removeRow(idx)}
                        index={idx}
                    />
                ))}
            </div>
        </div>
    );
}

function FitmentRow({ entry, vehicles, onChange, onRemove, index }) {
    const makes = Object.keys(vehicles);
    const models = useMemo(() => (entry.make && vehicles[entry.make] ? Object.keys(vehicles[entry.make]) : []), [entry.make, vehicles]);
    const availYears = useMemo(() => {
        if (entry.make && entry.model && vehicles[entry.make]?.[entry.model]) return vehicles[entry.make][entry.model].years || [];
        return [];
    }, [entry.make, entry.model, vehicles]);
    const availTrims = useMemo(() => {
        if (entry.make && entry.model && vehicles[entry.make]?.[entry.model]) return vehicles[entry.make][entry.model].trims || [];
        return [];
    }, [entry.make, entry.model, vehicles]);

    const toggleYear = (y) => {
        const cur = new Set(entry.years || []);
        if (cur.has(y)) cur.delete(y); else cur.add(y);
        onChange({ years: Array.from(cur).sort((a, b) => a - b) });
    };
    const toggleTrim = (t) => {
        const cur = new Set(entry.trims || []);
        if (cur.has(t)) cur.delete(t); else cur.add(t);
        onChange({ trims: Array.from(cur) });
    };

    const onMakeChange = (m) => {
        // reset model/years/trims when make changes
        const firstModel = m && vehicles[m] ? Object.keys(vehicles[m])[0] || '' : '';
        onChange({ make: m, model: firstModel, years: [], trims: [] });
    };
    const onModelChange = (m) => onChange({ model: m, years: [], trims: [] });

    return (
        <div className="rrm-panel p-3 bg-black/30 space-y-3" data-testid={`fitment-row-${index}`}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-white/40">Platform #{index + 1}</div>
                <button type="button" onClick={onRemove} data-testid={`fitment-remove-${index}`} className="p-1 rounded hover:bg-rose-500/15 text-rose-300">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <Lbl>Make</Lbl>
                    <select value={entry.make || ''} onChange={(e) => onMakeChange(e.target.value)} data-testid={`fitment-make-${index}`} className="w-full bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-sm">
                        <option value="">— select —</option>
                        {makes.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
                <div>
                    <Lbl>Model</Lbl>
                    <select value={entry.model || ''} onChange={(e) => onModelChange(e.target.value)} data-testid={`fitment-model-${index}`} disabled={!entry.make} className="w-full bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-sm disabled:opacity-50">
                        <option value="">— select —</option>
                        {models.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
            </div>

            <div>
                <Lbl>Years <Hint>(click to toggle{availYears.length > 0 ? '' : '; add this make/model in Vehicles tab to see options'})</Hint></Lbl>
                <div className="flex flex-wrap gap-1.5">
                    {availYears.map((y) => {
                        const on = (entry.years || []).includes(y);
                        return (
                            <button key={y} type="button" onClick={() => toggleYear(y)} data-testid={`fitment-year-${index}-${y}`} className={`text-[11px] px-2 py-1 rounded-md border ${on ? 'bg-amber-500/20 border-amber-500/40 text-amber-200' : 'border-white/10 text-white/60 hover:border-white/30'}`}>
                                {y}
                            </button>
                        );
                    })}
                </div>
                <CommaInput
                    placeholder="Or paste years: 2018, 2019, 2020"
                    value={(entry.years || []).join(', ')}
                    onChange={(s) => {
                        const arr = s.split(/[\s,]+/).map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
                        onChange({ years: Array.from(new Set(arr)).sort((a, b) => a - b) });
                    }}
                    testid={`fitment-years-text-${index}`}
                />
            </div>

            <div>
                <Lbl>Trims <Hint>(click to toggle; leave empty to mean "all trims")</Hint></Lbl>
                <div className="flex flex-wrap gap-1.5">
                    {availTrims.map((t) => {
                        const on = (entry.trims || []).includes(t);
                        return (
                            <button key={t} type="button" onClick={() => toggleTrim(t)} data-testid={`fitment-trim-${index}-${t}`} className={`text-[11px] px-2 py-1 rounded-md border ${on ? 'bg-amber-500/20 border-amber-500/40 text-amber-200' : 'border-white/10 text-white/60 hover:border-white/30'}`}>
                                {t}
                            </button>
                        );
                    })}
                </div>
                <CommaInput
                    placeholder="Or paste trims: R/T, Scat Pack, SRT Hellcat"
                    value={(entry.trims || []).join(', ')}
                    onChange={(s) => onChange({ trims: s.split(',').map((x) => x.trim()).filter(Boolean) })}
                    testid={`fitment-trims-text-${index}`}
                />
            </div>

            <div>
                <Lbl>Engines <Hint>(free text, comma-separated)</Hint></Lbl>
                <CommaInput
                    placeholder="5.7L V8, 6.4L V8, 6.2L Supercharged V8"
                    value={(entry.engines || []).join(', ')}
                    onChange={(s) => onChange({ engines: s.split(',').map((x) => x.trim()).filter(Boolean) })}
                    testid={`fitment-engines-text-${index}`}
                />
            </div>
        </div>
    );
}

function Lbl({ children }) { return <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{children}</div>; }
function Hint({ children }) { return <span className="text-white/30 normal-case tracking-normal text-[10px]">{children}</span>; }
function CommaInput({ value, onChange, placeholder, testid }) {
    return (
        <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testid}
            className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs"
        />
    );
}
